// src/utils/raceDay.js

// 0=Sun … 6=Sat
const IDX2DAY = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

/** Mon→Sun (matches plan schedule keys and UI order). */
const WEEK_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** Always derive weekday from the *local* Y-M-D, ignoring time and timezone. */
function localWeekdayFromString(dateStr) {
  if (typeof dateStr !== "string") return null;

  // Grab the Y-M-D from the first 10 chars if it's ISO-like
  // Works for "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss(.sss)[Z]"
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1; // 0-based
    const d = Number(m[3]);
    const local = new Date(y, mo, d); // local midnight, no TZ shift
    return local.getDay(); // 0..6
  }

  // Fallback: try native parsing, then use local weekday
  const dt = new Date(dateStr);
  return Number.isNaN(dt.getTime()) ? null : dt.getDay();
}

/**
 * Places exactly one "RACE DAY!" label on the correct weekday in the final week.
 * - Removes any existing race-day labels in that week first
 * - Sets final week focus = "Race Week"
 * - Optionally clears any workouts *after* race day
 */
export function placeRaceDayOnCorrectWeekday(
  plan,
  raceDateStr,
  label,
  {
    clearAfter = false,

    // New options (safe defaults — prefer unit-aware strings from planSwitcher, e.g. Easy Run (3 km))
    cleanRaceWeek = true,
    replaceHardWith = "Easy Run (2 mi)",
    replaceLongRunWith = "Easy Run (2 mi)",
  } = {}
) {
  if (!Array.isArray(plan) || !plan.length) return plan;

  const dayIdx = localWeekdayFromString(raceDateStr);
  if (dayIdx == null) return plan;

  const raceDayName = IDX2DAY[dayIdx];
  const lastWeek = plan[plan.length - 1];
  const sched = lastWeek.schedule || {};

  // 1) Remove any existing race-day markers anywhere in the week
  for (const d of Object.keys(sched)) {
    if (typeof sched[d] === "string" && /race day/i.test(sched[d])) {
      delete sched[d];
    }
  }

  // 2) Set the correct weekday for the race
  sched[raceDayName] = label;

  // 3) Clean up the rest of Race Week (remove intervals/tempo/long run)
  if (cleanRaceWeek) {
    for (const d of Object.keys(sched)) {
      if (d === raceDayName) continue;

      const v = sched[d];
      if (typeof v !== "string") continue;

      const lower = v.toLowerCase();

      // Remove hard workouts in race week
      if (lower.includes("interval") || lower.includes("tempo")) {
        sched[d] = replaceHardWith;
        continue;
      }

      // Long run should not exist in race week
      if (lower.includes("long run")) {
        sched[d] = replaceLongRunWith;
        continue;
      }
    }
  }

  // 4) (Optional) no runs after race day — Rest on all week days listed after race (Mon→Sun order)
  if (clearAfter) {
    const pos = WEEK_ORDER.indexOf(raceDayName);
    if (pos >= 0) {
      for (let i = pos + 1; i < WEEK_ORDER.length; i++) {
        const d = WEEK_ORDER[i];
        if (sched[d]) sched[d] = "Rest";
      }
    }
  }

  lastWeek.schedule = sched;
  lastWeek.focus = "Race Week";
  return plan;
}

